<template>
  <div class="applications-chart-wrapper">
    <!-- Debug Info -->
    <div v-if="debugMode" class="debug-info">
      <h5>Debug Info:</h5>
      <pre>{{ JSON.stringify(applicationsByType, null, 2) }}</pre>
      <p>hasData: {{ hasData }}</p>
      <p>totalApplications: {{ totalApplications }}</p>
    </div>

    <!-- Resumo dos Dados -->
    <div v-if="hasData" class="chart-summary">
      <div class="summary-header">
        <h4 class="summary-title">
          <i class="fas fa-layer-group me-2"></i>
          Resumo das Aplicações
        </h4>
        <div class="total-count">
          <span class="total-number">{{ totalApplications }}</span>
          <span class="total-label">{{ totalApplications === 1 ? 'aplicação' : 'aplicações' }}</span>
        </div>
      </div>
      
      <div class="summary-items">
        <div 
          v-for="(count, type, index) in currentData" 
          :key="type"
          class="summary-item"
        >
          <div class="item-indicator" :style="{ backgroundColor: getTypeColor(type) }"></div>
          <div class="item-info">
            <span class="item-name">{{ getTypeName(type) }}</span>
            <div class="item-stats">
              <span class="item-count">{{ count }}</span>
              <span class="item-percentage">({{ getPercentage(count) }}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gráfico -->
    <div class="chart-container">
      <div v-if="!hasData" class="no-data-state">
        <div class="no-data-icon">
          <i class="fas fa-search"></i>
        </div>
        <h5>Nenhuma Aplicação Detectada</h5>
        <p>O sistema ainda não encontrou aplicações neste servidor</p>
        <small class="debug-hint" @click="toggleDebug">
          🔧 Clique aqui para ver informações de debug (irá mostrar dados de teste)
        </small>
      </div>
      
      <!-- Canvas do gráfico -->
      <canvas v-else ref="chartCanvas" class="chart-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'

export default {
  name: 'ApplicationsChart',
  props: {
    applicationsByType: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      debugMode: false,
      chart: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 55,
        animation: {
          animateRotate: true,
          animateScale: true,
          duration: 1200
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            fontColor: '#495057',
            usePointStyle: true,
            padding: 20,
            fontSize: 13,
            fontStyle: 'bold',
            generateLabels: (chart) => {
              const data = chart.data
              if (data.labels.length && data.datasets.length) {
                const dataset = data.datasets[0]
                const total = dataset.data.reduce((sum, val) => sum + val, 0)
                
                return data.labels.map((label, i) => {
                  const value = dataset.data[i]
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                  
                  return {
                    text: `${label}: ${value} (${percentage}%)`,
                    fillStyle: dataset.backgroundColor[i],
                    strokeStyle: dataset.borderColor,
                    lineWidth: dataset.borderWidth,
                    pointStyle: 'circle',
                    hidden: false,
                    index: i
                  }
                })
              }
              return []
            }
          }
        },
        tooltips: {
          backgroundColor: 'rgba(33, 37, 41, 0.95)',
          titleFontColor: 'white',
          titleFontSize: 15,
          titleFontStyle: 'bold',
          bodyFontColor: 'white',
          bodyFontSize: 14,
          borderColor: 'rgba(255,255,255,0.3)',
          borderWidth: 1,
          cornerRadius: 10,
          displayColors: true,
          xPadding: 15,
          yPadding: 12,
          callbacks: {
            title: function(tooltipItem, data) {
              return data.labels[tooltipItem[0].index]
            },
            label: function(tooltipItem, data) {
              const value = data.datasets[0].data[tooltipItem.index]
              const total = data.datasets[0].data.reduce((sum, val) => sum + val, 0)
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
              return `Aplicações: ${value} (${percentage}%)`
            },
            afterLabel: function(tooltipItem, data) {
              const value = data.datasets[0].data[tooltipItem.index]
              return value === 1 ? 'aplicação detectada' : 'aplicações detectadas'
            }
          }
        }
      }
    }
  },
  computed: {
    hasData() {
      const applications = this.applicationsByType || {}
      const data = Object.values(applications)
      
      // Se está em modo debug ou tem dados reais, mostra o gráfico
      const result = this.debugMode || (data.length > 0 && !data.every(val => val === 0))
      console.log('ApplicationsChart - hasData:', result, 'applicationsByType:', applications, 'debugMode:', this.debugMode)
      return result
    },
    
    totalApplications() {
      const currentData = this.currentData
      const total = Object.values(currentData).reduce((sum, count) => sum + count, 0)
      console.log('ApplicationsChart - totalApplications:', total)
      return total
    },
    
    currentData() {
      const applications = this.applicationsByType || {}
      
      // Se não há dados reais mas está em debug, usa dados de teste
      if (Object.keys(applications).length === 0 && this.debugMode) {
        const testData = { 'laravel': 3, 'vuejs': 2, 'react': 1, 'unknown': 1 }
        console.log('ApplicationsChart - usando dados de teste:', testData)
        return testData
      }
      
      console.log('ApplicationsChart - usando dados reais:', applications)
      return applications
    },
    
    chartData() {
      const dataToUse = this.currentData
      const labels = Object.keys(dataToUse)
      const data = Object.values(dataToUse)
      
      console.log('ApplicationsChart - chartData:', { dataToUse, labels, data })
      
      if (labels.length === 0 || data.every(val => val === 0)) {
        return {
          labels: ['Nenhuma aplicação detectada'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e9ecef'],
            borderColor: ['#dee2e6'],
            borderWidth: 2
          }]
        }
      }
      
      // Mapeia labels para nomes mais amigáveis
      const friendlyLabels = {
        'laravel': 'Laravel',
        'vuejs': 'Vue.js',
        'vue': 'Vue.js',
        'react': 'React',
        'angular': 'Angular',
        'nodejs': 'Node.js',
        'node': 'Node.js',
        'nextjs': 'Next.js',
        'nuxtjs': 'Nuxt.js',
        'php': 'PHP',
        'symfony': 'Symfony',
        'codeigniter': 'CodeIgniter',
        'cakephp': 'CakePHP',
        'python': 'Python',
        'django': 'Django',
        'flask': 'Flask',
        'fastapi': 'FastAPI',
        'java': 'Java',
        'springboot': 'Spring Boot',
        'quarkus': 'Quarkus',
        'dotnet': '.NET',
        'html': 'HTML/CSS',
        'javascript': 'JavaScript',
        'typescript': 'TypeScript',
        'unknown': 'Outros'
      }
      
      return {
        labels: labels.map(label => friendlyLabels[label.toLowerCase()] || label.charAt(0).toUpperCase() + label.slice(1)),
        datasets: [{
          data: data,
          backgroundColor: labels.map(label => this.getTypeColor(label)),
          borderColor: '#ffffff',
          borderWidth: 3,
          hoverBorderWidth: 5,
          hoverBorderColor: '#ffffff',
          hoverBackgroundColor: labels.map(label => {
            const color = this.getTypeColor(label)
            return this.darkenColor(color, 0.15)
          })
        }]
      }
    }
  },
  mounted() {
    console.log('ApplicationsChart - mounted with props:', this.applicationsByType)
    this.$nextTick(() => {
      if (this.hasData) {
        this.createChart()
      }
    })
  },
  watch: {
    applicationsByType: {
      handler(newVal, oldVal) {
        console.log('ApplicationsChart - applicationsByType changed:', newVal, 'from:', oldVal)
        this.updateChart()
      },
      deep: true
    },
    debugMode() {
      this.updateChart()
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    toggleDebug() {
      this.debugMode = !this.debugMode
    },

    createChart() {
      if (!this.$refs.chartCanvas) {
        console.log('ApplicationsChart - canvas ref não encontrado')
        return
      }

      const ctx = this.$refs.chartCanvas.getContext('2d')
      
      if (this.chart) {
        this.chart.destroy()
      }

      // Plugin para desenhar os números nas fatias
      const drawLabelsPlugin = {
        afterDraw: (chart) => {
          this.drawDataLabels(chart)
        }
      }

      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: this.chartData,
        options: this.chartOptions,
        plugins: [drawLabelsPlugin]
      })

      console.log('ApplicationsChart - gráfico criado')
    },

    updateChart() {
      console.log('ApplicationsChart - updateChart called')
      
      this.$nextTick(() => {
        if (this.hasData) {
          console.log('ApplicationsChart - criando/atualizando gráfico')
          this.createChart()
        } else {
          console.log('ApplicationsChart - destruindo gráfico - sem dados')
          if (this.chart) {
            this.chart.destroy()
            this.chart = null
          }
        }
      })
    },
    
    // Desenha números nas fatias do gráfico
    drawDataLabels(chart) {
      const ctx = chart.ctx
      const meta = chart.getDatasetMeta(0)
      
      if (!meta.data || meta.data.length === 0) return
      
      const total = chart.data.datasets[0].data.reduce((sum, val) => sum + val, 0)
      
      meta.data.forEach((element, index) => {
        const model = element._model
        const value = chart.data.datasets[0].data[index]
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
        
        // Só desenha se a fatia for maior que 10% para evitar sobreposição
        if (percentage > 10) {
          const x = model.x
          const y = model.y
          
          // Configuração do texto
          ctx.save()
          ctx.fillStyle = '#ffffff'
          ctx.strokeStyle = '#333333'
          ctx.lineWidth = 1
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          
          // Desenha o número principal
          ctx.font = 'bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
          ctx.strokeText(value, x, y - 5)
          ctx.fillText(value, x, y - 5)
          
          // Desenha o percentual
          ctx.font = 'normal 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
          ctx.strokeText(`(${percentage}%)`, x, y + 12)
          ctx.fillText(`(${percentage}%)`, x, y + 12)
          
          ctx.restore()
        }
      })
    },
    
    getTypeColor(type) {
      const colors = {
        'laravel': '#FF2D20',
        'vuejs': '#4FC08D', 
        'vue': '#4FC08D',
        'react': '#61DBFB',
        'angular': '#DD0031',
        'nodejs': '#68A063',
        'node': '#68A063',
        'nextjs': '#000000',
        'nuxtjs': '#00C58E',
        'php': '#777BB4',
        'symfony': '#000000',
        'codeigniter': '#EE4623',
        'cakephp': '#D33C43',
        'python': '#3776AB',
        'django': '#092E20',
        'flask': '#000000',
        'fastapi': '#009688',
        'java': '#ED8B00',
        'springboot': '#6DB33F',
        'quarkus': '#4695EB',
        'dotnet': '#512BD4',
        'html': '#E34C26',
        'javascript': '#F7DF1E',
        'typescript': '#3178C6',
        'outros': '#95A5A6',
        'other': '#95A5A6',
        'unknown': '#BDC3C7'
      }
      return colors[type.toLowerCase()] || colors['unknown']
    },
    
    getTypeName(type) {
      const friendlyLabels = {
        'laravel': 'Laravel',
        'vuejs': 'Vue.js',
        'vue': 'Vue.js',
        'react': 'React',
        'angular': 'Angular',
        'nodejs': 'Node.js',
        'node': 'Node.js',
        'nextjs': 'Next.js',
        'nuxtjs': 'Nuxt.js',
        'php': 'PHP',
        'symfony': 'Symfony',
        'codeigniter': 'CodeIgniter',
        'cakephp': 'CakePHP',
        'python': 'Python',
        'django': 'Django',
        'flask': 'Flask',
        'fastapi': 'FastAPI',
        'java': 'Java',
        'springboot': 'Spring Boot',
        'quarkus': 'Quarkus',
        'dotnet': '.NET',
        'html': 'HTML/CSS',
        'javascript': 'JavaScript',
        'typescript': 'TypeScript',
        'unknown': 'Outros'
      }
      return friendlyLabels[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1)
    },
    
    getPercentage(count) {
      const total = this.totalApplications
      return total > 0 ? ((count / total) * 100).toFixed(1) : 0
    },
    
    // Função para escurecer uma cor
    darkenColor(color, amount) {
      const usePound = color[0] === '#'
      const col = usePound ? color.slice(1) : color
      
      const num = parseInt(col, 16)
      let r = Math.floor((num >> 16) * (1 - amount))
      let g = Math.floor(((num >> 8) & 0x00FF) * (1 - amount))
      let b = Math.floor((num & 0x0000FF) * (1 - amount))
      
      r = r < 0 ? 0 : r > 255 ? 255 : r
      g = g < 0 ? 0 : g > 255 ? 255 : g
      b = b < 0 ? 0 : b > 255 ? 255 : b
      
      return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
    }
  }
}
</script>

<style scoped>
.applications-chart-wrapper {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.applications-chart-wrapper:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* Debug Info */
.debug-info {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  font-family: monospace;
  font-size: 12px;
}

.debug-hint {
  color: #6c757d;
  cursor: pointer;
  margin-top: 10px;
  display: block;
}

.debug-hint:hover {
  color: #495057;
  text-decoration: underline;
}

/* Resumo dos Dados */
.chart-summary {
  margin-bottom: 25px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  color: white;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.summary-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.total-count {
  text-align: right;
}

.total-number {
  font-size: 2rem;
  font-weight: 700;
  display: block;
  line-height: 1;
}

.total-label {
  font-size: 0.85rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-items {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
}

.summary-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  padding: 12px 15px;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.summary-item:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.item-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-weight: 600;
  font-size: 0.9rem;
  display: block;
  margin-bottom: 2px;
}

.item-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-count {
  font-weight: 700;
  font-size: 1.1rem;
}

.item-percentage {
  font-size: 0.85rem;
  opacity: 0.9;
}

/* Container do Gráfico */
.chart-container {
  position: relative;
  height: 350px;
}

.chart-canvas {
  border-radius: 12px;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
}

.chart-canvas:hover {
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.15));
}

/* Estado Vazio */
.no-data-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.no-data-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #64748b;
}

.no-data-state h5 {
  margin-bottom: 8px;
  color: #374151;
  font-weight: 600;
}

.no-data-state p {
  margin: 0;
  font-size: 0.95rem;
}

/* Animações */
.chart-canvas {
  animation: fadeInScale 0.8s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .applications-chart-wrapper {
    padding: 15px;
    border-radius: 12px;
  }
  
  .summary-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .summary-items {
    grid-template-columns: 1fr;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .chart-canvas {
    border-radius: 8px;
  }
}

@media (max-width: 480px) {
  .applications-chart-wrapper {
    padding: 10px;
    margin: 5px;
  }
  
  .chart-summary {
    padding: 15px;
  }
  
  .total-number {
    font-size: 1.5rem;
  }
  
  .chart-container {
    height: 250px;
  }
}
</style> 